import React, { useState, useMemo } from 'react';
import { ChevronLeft, Search, Activity, Users, Percent, ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react';

const PlayerParticipationReport = ({ players, games, tournaments = [], onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTournament, setSelectedTournament] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'percentage', direction: 'desc' });

  // Calculate participation stats
  const participationStats = useMemo(() => {
    // Only consider past/completed games, or all games? 
    // Usually availability and squad selections are relevant for upcoming games too.
    // Let's use all games.
    
    // Filter games by tournament if one is selected
    const filteredGames = selectedTournament === 'all' 
      ? games 
      : games.filter(g => g.tournamentId === selectedTournament);

    return players.map(player => {
      let gamesAvailable = 0;
      let gamesSelected = 0;

      filteredGames.forEach(game => {
        // Did the player explicitly say they are available?
        const isAvailable = game.availability?.[player.id]?.status === 'available';
        
        // Was the player selected in the squad?
        const isSelected = game.playing11?.includes(player.id) || game.superSubs?.includes(player.id);

        if (isAvailable) {
          gamesAvailable++;
        }

        if (isSelected) {
          gamesSelected++;
        }
      });

      const percentage = gamesAvailable > 0 
        ? Math.round((gamesSelected / gamesAvailable) * 100) 
        : 0;

      return {
        id: player.id,
        name: player.name,
        email: player.email,
        gamesAvailable,
        gamesSelected,
        percentage
      };
    });
  }, [players, games, selectedTournament]);

  // Filter and sort
  const filteredAndSortedStats = useMemo(() => {
    let result = participationStats;

    // Filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(stat => 
        stat.name?.toLowerCase().includes(lowerSearch) || 
        stat.email?.toLowerCase().includes(lowerSearch)
      );
    }

    // Sort
    result.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return result;
  }, [participationStats, searchTerm, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="text-gray-400" />;
    return sortConfig.direction === 'asc' ? <ChevronUp size={14} className="text-blue-600" /> : <ChevronDown size={14} className="text-blue-600" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronLeft size={24} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Participation Stats</h1>
          <p className="text-gray-600">Track player availability vs. squad selection</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Players</p>
              <h3 className="text-3xl font-bold text-gray-900 mt-1">{players.length}</h3>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
              <Users size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Games</p>
              <h3 className="text-3xl font-bold text-gray-900 mt-1">{games.length}</h3>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-green-600">
              <Activity size={24} />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-gray-500">Avg Selection Rate</p>
              <h3 className="text-3xl font-bold text-gray-900 mt-1">
                {participationStats.filter(s => s.gamesAvailable > 0).length > 0 
                  ? Math.round(participationStats.reduce((acc, curr) => acc + curr.percentage, 0) / participationStats.filter(s => s.gamesAvailable > 0).length) 
                  : 0}%
              </h3>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-purple-600">
              <Percent size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <select
              value={selectedTournament}
              onChange={(e) => setSelectedTournament(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[200px]"
            >
              <option value="all">All Tournaments</option>
              {tournaments.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th 
                  className="p-4 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('name')}
                >
                  <div className="flex items-center gap-2">
                    Player {getSortIcon('name')}
                  </div>
                </th>
                <th 
                  className="p-4 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('gamesAvailable')}
                >
                  <div className="flex items-center gap-2">
                    Times Available {getSortIcon('gamesAvailable')}
                  </div>
                </th>
                <th 
                  className="p-4 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('gamesSelected')}
                >
                  <div className="flex items-center gap-2">
                    Squad Selections {getSortIcon('gamesSelected')}
                  </div>
                </th>
                <th 
                  className="p-4 font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('percentage')}
                >
                  <div className="flex items-center gap-2">
                    Selection % {getSortIcon('percentage')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAndSortedStats.length > 0 ? (
                filteredAndSortedStats.map(stat => (
                  <tr key={stat.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <div className="font-medium text-gray-900">{stat.name}</div>
                      <div className="text-sm text-gray-500">{stat.email}</div>
                    </td>
                    <td className="p-4">
                      <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                        {stat.gamesAvailable}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-green-50 text-green-700 font-medium">
                        {stat.gamesSelected}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <span className={`font-bold ${
                          stat.gamesAvailable === 0 ? 'text-gray-400' :
                          stat.percentage >= 80 ? 'text-green-600' :
                          stat.percentage >= 50 ? 'text-orange-500' : 'text-red-500'
                        }`}>
                          {stat.gamesAvailable === 0 ? '-' : `${stat.percentage}%`}
                        </span>
                        
                        {stat.gamesAvailable > 0 && (
                          <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${
                                stat.percentage >= 80 ? 'bg-green-500' :
                                stat.percentage >= 50 ? 'bg-orange-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(100, stat.percentage)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="p-8 text-center text-gray-500">
                    No players found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlayerParticipationReport;
